import { memo, type ReactNode } from 'react';
import { Section } from '@/components/ui';
import type { SelectedBooking } from '@/context/TablesContext';
import { firstName, formatClock, formatINR, formatShortDuration } from '@/utils/format';
import { PartInput } from './PartInput';
import styles from '@/styles/components/BillBookingsTable/style.scss';
import split from '@/styles/components/BillSplit/style.scss';

/** A booking as the table shows it: a ticked booking, or a saved bill's line (mapped to this shape). */
export type BillBookingRow = Pick<
  SelectedBooking,
  'id' | 'tableName' | 'customerName' | 'startedAt' | 'endedAt' | 'accumulatedMs' | 'pausedMs' | 'pauseAmount' | 'amount'
> & {
  /** A saved bill's line: the booking's whole amount, more than amount when it was split across bills. */
  bookingAmount?: number;
  /** Doubles: the booking's second name. With it the row shows first names only ("Aditya & Vikram"). */
  customerName2?: string;
};

/** The name cell: the name, or both first names for a doubles booking, as on the table cards. */
function rowNames(s: BillBookingRow): string {
  if (!s.customerName2?.trim()) return s.customerName;
  return `${firstName(s.customerName) || 'No name'} & ${firstName(s.customerName2)}`;
}

/** Full names, for the tooltip of a doubles booking. */
function fullNames(s: BillBookingRow): string | undefined {
  return s.customerName2?.trim() ? [s.customerName, s.customerName2].filter((n) => n.trim()).join(' & ') : undefined;
}

/**
 * The bookings on the bill. Shows a Table column when they come from more than one table (the bill modal's header
 * names a single table), or always with `showTable` (Bill View, whose header doesn't), and a Pause column (minutes
 * and what they cost, already in Amount) when any booking was paused. Memoised: the bookings are fixed while the
 * bill is open, so typing in the bill never re-renders this table.
 *
 * Split (the bill modal with several bills): `parts` is each booking's amount on the bill shown, edited in a
 * "This bill" column; the rest of each booking goes to the other bills.
 */
export const BillBookingsTable = memo(function BillBookingsTable({
  bookings,
  showTable = false,
  action,
  parts,
  onPartChange,
}: {
  bookings: BillBookingRow[];
  showTable?: boolean;
  /** Beside the section title (the bill modal's Split button). */
  action?: ReactNode;
  parts?: Record<string, number> | null;
  onPartChange?: (bookingId: string, value: number) => void;
}) {
  const multiTable = showTable || new Set(bookings.map((b) => b.tableName)).size > 1;
  const anyPause = bookings.some((b) => b.pausedMs > 0);
  const splitting = !!parts && !!onPartChange;

  return (
    <Section title="Bookings" action={action}>
      <div className={styles.frame}>
        <table className={styles.table}>
          <thead>
            <tr>
              {multiTable && <th>Table</th>}
              <th>Name</th>
              <th>Start</th>
              <th>End</th>
              <th>Duration</th>
              {anyPause && <th>Pause</th>}
              <th className={styles.num}>Amount</th>
              {splitting && <th className={styles.num}>This bill</th>}
            </tr>
          </thead>
          <tbody>
            {bookings.map((s) => {
              const whole = s.bookingAmount ?? s.amount ?? 0;
              const isPart = !splitting && whole > (s.amount ?? 0);
              const names = rowNames(s);
              return (
                <tr key={s.id}>
                  {multiTable && <td className={styles.data}>{s.tableName}</td>}
                  <td className={names ? styles.name : styles.unnamed} title={fullNames(s)}>
                    {names || 'Unnamed'}
                  </td>
                  <td className={styles.data}>{formatClock(s.startedAt)}</td>
                  <td className={styles.data}>{s.endedAt ? formatClock(s.endedAt) : '—'}</td>
                  <td className={styles.duration}>{formatShortDuration(s.accumulatedMs)}</td>
                  {anyPause && (
                    <td className={styles.pause}>
                      {s.pausedMs > 0 ? `${formatShortDuration(s.pausedMs)} · ${formatINR(s.pauseAmount ?? 0)}` : '—'}
                    </td>
                  )}
                  <td className={styles.money}>
                    {formatINR(s.amount ?? 0)}
                    {/* A saved bill's part of a booking split between teams. */}
                    {isPart && <span className={split.whole}>of {formatINR(whole)} · split</span>}
                  </td>
                  {splitting && (
                    <td>
                      <PartInput
                        value={parts[s.id] ?? 0}
                        max={s.amount ?? 0}
                        onChange={(v) => onPartChange(s.id, v)}
                        aria-label={`This bill's part of ${names || 'the booking'} at ${formatClock(s.startedAt)}`}
                        className={split.partInput}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
});
